import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { 
  FileText, Copy, Save, ChevronRight, ChevronDown,
  Info, Hash, List, Type, Calendar, Users,
  Edit, RefreshCw, MessageSquare, Plus
} from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useScenarioStore } from '../../../../stores/scenarioStore'
import type { ScenarioInput } from '@creanizer/types'

interface ScenarioResultProps {
  result: any // 動的な構造に対応するためany型を使用
  originalPrompt?: ScenarioInput // ScenarioInput型に変更
  onResultUpdate?: (result: any) => void // 結果更新時のコールバック
}

export function ScenarioResult({ result, originalPrompt, onResultUpdate }: ScenarioResultProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState('overview')
  const [mainTab, setMainTab] = useState('view')
  const [isSaved, setIsSaved] = useState(false)
  const [currentResult, setCurrentResult] = useState(result)
  const [additionalPrompt, setAdditionalPrompt] = useState('')
  const [correctionInstruction, setCorrectionInstruction] = useState('')
  const [isRefining, setIsRefining] = useState(false)
  const generator = useGenerator('scenario')
  const { addScenario } = useScenarioStore()
  
  const toggleSection = (key: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedSections(newExpanded)
  }
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }
  
  const handleSave = () => {
    const basicInfo = getBasicInfo()
    addScenario({
      title: basicInfo.title,
      input: originalPrompt || { mode: 'generate' as const },
      result: currentResult,
      category: basicInfo.genre || 'fantasy',
      tags: []
    })
    console.log('シナリオを保存しました')
    setIsSaved(true)
  }
  
  // 追加生成処理
  const handleAdditionalGeneration = async () => {
    if (!additionalPrompt.trim()) return
    
    setIsRefining(true)
    try {
      const basicInfo = getBasicInfo()
      const refinedInput = {
        mode: 'generate_scenario' as const,
        prompt: `【既存のシナリオ】\n${JSON.stringify(currentResult, null, 2)}\n\n【追加で生成してほしい内容】\n${additionalPrompt}\n\n上記の既存シナリオをベースに、追加内容を統合して改善してください。`,
        worldContext: {
          worldName: basicInfo.title,
          description: basicInfo.summary
        }
      }
      
      const response = await generator.mutateAsync(refinedInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult)
        if (onResultUpdate) {
          onResultUpdate(newResult)
        }
        setIsSaved(false)
        console.log('追加生成を実行し、結果を更新しました')
        setAdditionalPrompt('')
        setMainTab('view')
      }
    } catch (error) {
      console.error('追加生成エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  // 修正指示処理
  const handleCorrection = async () => {
    if (!correctionInstruction.trim()) return
    
    setIsRefining(true)
    try {
      const basicInfo = getBasicInfo()
      const correctionInput = {
        mode: 'generate_scenario' as const,
        prompt: `【現在のシナリオ】\n${JSON.stringify(currentResult, null, 2)}\n\n【修正指示】\n${correctionInstruction}\n\n上記の修正指示に従って、シナリオを修正してください。`,
        worldContext: {
          worldName: basicInfo.title,
          description: basicInfo.summary
        }
      }
      
      const response = await generator.mutateAsync(correctionInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult)
        if (onResultUpdate) {
          onResultUpdate(newResult)
        }
        setIsSaved(false)
        console.log('修正指示を実行し、結果を更新しました')
        setCorrectionInstruction('')
        setMainTab('view')
      }
    } catch (error) {
      console.error('修正処理エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  // 基本情報を柔軟に取得
  const getBasicInfo = () => {
    const info: any = {}
    
    // タイトル
    info.title = result.title || result.basicInfo?.title || result.name || result.scenarioTitle || 'タイトル未設定'
    
    // その他の基本情報
    info.system = result.system || result.basicInfo?.system || result.systemName || ''
    info.playerCount = result.playerCount || result.basicInfo?.playerCount || result.players || ''
    info.playTime = result.playTime || result.basicInfo?.playTime || result.duration || ''
    info.difficulty = result.difficulty || result.basicInfo?.difficulty || result.level || ''
    info.summary = result.summary || result.basicInfo?.summary || result.description || result.trailer || ''
    info.genre = result.genre || result.basicInfo?.genre || result.category || ''
    
    return info
  }
  
  // 標準フィールドかどうかを判定
  const isStandardField = (key: string): boolean => {
    const standardFields = [
      'basicInfo', 'title', 'system', 'playerCount', 'playTime', 
      'difficulty', 'summary', 'genre', 'name', 'scenarioTitle',
      'systemName', 'players', 'duration', 'level', 'description',
      'trailer', 'category', 'metadata'
    ]
    return standardFields.includes(key)
  }
  
  // 値を適切な形式で表示
  const renderValue = (value: any, depth: number = 0): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400">-</span>
    }
    
    if (typeof value === 'string') {
      return <div className="text-gray-700 whitespace-pre-wrap">{value}</div>
    }
    
    if (typeof value === 'number' || typeof value === 'boolean') {
      return <span className="font-mono">{String(value)}</span>
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400">（なし）</span>
      }
      if (typeof value[0] === 'string' || typeof value[0] === 'number') {
        return (
          <ul className="list-disc list-inside space-y-1">
            {value.map((item, i) => (
              <li key={i} className="text-gray-700">{String(item)}</li>
            ))}
          </ul>
        )
      }
      return (
        <div className="space-y-2">
          {value.map((item, i) => (
            <Card key={i} className="p-3">
              {renderValue(item, depth + 1)}
            </Card>
          ))}
        </div>
      )
    }
    
    if (typeof value === 'object') {
      return (
        <div className="space-y-3">
          {Object.entries(value).map(([k, v]) => (
            <div key={k}>
              <h5 className="font-medium text-sm text-gray-600 mb-1 capitalize">
                {k.replace(/([A-Z])/g, ' $1').trim()}
              </h5>
              <div className="pl-4">
                {renderValue(v, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      )
    }
    
    return <span>{JSON.stringify(value)}</span>
  }
  
  // フィールド名を日本語に変換
  const translateFieldName = (key: string): string => {
    const translations: Record<string, string> = {
      scenes: 'シーン',
      npcs: 'NPC',
      items: 'アイテム',
      locations: '場所',
      handouts: 'ハンドアウト',
      investigation: '調査情報',
      investigationPhase: '調査フェーズ',
      investigationLocations: '調査場所',
      climax: 'クライマックス',
      climaxPhase: 'クライマックスフェーズ',
      ending: 'エンディング',
      endings: 'エンディング',
      gmNotes: 'GM用情報',
      events: 'イベント',
      structure: '構造',
      content: 'コンテンツ',
      handoutDistribution: 'ハンドアウト配布',
      truthAndConclusion: '真相と結末',
      background: '背景',
      hook: '導入',
      mainPhase: 'メインフェーズ',
      introduction: '導入',
      introductionPhase: '導入フェーズ',
      setting: '設定',
      madness: '狂気',
      prizes: '報酬',
      enemies: '敵',
      sceneChart: 'シーンチャート',
      handoutFlow: 'ハンドアウトの流れ',
      scenarioGoal: 'シナリオの目的',
      scenarioSpec: 'シナリオ仕様'
    }
    return translations[key] || key
  }
  
  const basicInfo = getBasicInfo()
  
  // すべてのフィールドを取得（標準フィールドを除く）
  const dynamicFields = Object.entries(currentResult).filter(([key]) => !isStandardField(key))
  
  // タブに表示するフィールドを決定
  const tabs = [
    { key: 'overview', label: '概要', alwaysShow: true },
    ...dynamicFields.map(([key]) => ({
      key,
      label: translateFieldName(key),
      alwaysShow: false
    }))
  ]
  
  return (
    <div className="space-y-6">
      {/* メインタブナビゲーション */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="view">
            <FileText className="w-4 h-4 mr-2" />
            結果表示
          </TabsTrigger>
          <TabsTrigger value="refine">
            <RefreshCw className="w-4 h-4 mr-2" />
            追加生成
          </TabsTrigger>
          <TabsTrigger value="correct">
            <Edit className="w-4 h-4 mr-2" />
            修正指示
          </TabsTrigger>
        </TabsList>
        
        {/* 結果表示タブ */}
        <TabsContent value="view" className="space-y-6">
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{basicInfo.title}</CardTitle>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
              {basicInfo.system && (
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {basicInfo.system}
                </span>
              )}
              {basicInfo.playerCount && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {basicInfo.playerCount}
                </span>
              )}
              {basicInfo.playTime && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {basicInfo.playTime}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}>
              <Copy className="w-4 h-4 mr-1" />
              コピー
            </Button>
            <Button 
              size="sm" 
              variant={isSaved ? 'secondary' : 'outline'}
              onClick={handleSave}
              disabled={isSaved}
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4">
          {basicInfo.genre && <Badge>{basicInfo.genre}</Badge>}
          {basicInfo.difficulty && <Badge variant="secondary">{basicInfo.difficulty}</Badge>}
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <ScrollArea className="w-full overflow-x-auto">
            <TabsList className="mb-6">
              {tabs.map(tab => (
                <TabsTrigger key={tab.key} value={tab.key}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </ScrollArea>
          
          {/* 概要タブ */}
          <TabsContent value="overview" className="space-y-6">
            {basicInfo.summary && (
              <div>
                <h3 className="font-semibold mb-2">概要</h3>
                <div className="text-gray-700 whitespace-pre-wrap">{basicInfo.summary}</div>
              </div>
            )}
            
            {/* 基本情報以外の主要フィールドを表示 */}
            <div className="space-y-4">
              {dynamicFields.slice(0, 5).map(([key, value]) => (
                <div key={key}>
                  <h3 className="font-semibold mb-2 flex items-center justify-between">
                    {translateFieldName(key)}
                    <button
                      onClick={() => toggleSection(key)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {expandedSections.has(key) ? 
                        <ChevronDown className="w-4 h-4" /> : 
                        <ChevronRight className="w-4 h-4" />
                      }
                    </button>
                  </h3>
                  {expandedSections.has(key) && (
                    <div className="mt-2 pl-4 border-l-2 border-gray-200">
                      {renderValue(value)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
          
          {/* 動的フィールドのタブ */}
          {dynamicFields.map(([key, value]) => (
            <TabsContent key={key} value={key} className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4">{translateFieldName(key)}</h3>
                <div className="space-y-4">
                  {renderValue(value)}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
        </TabsContent>
        
        {/* 追加生成タブ */}
        <TabsContent value="refine" className="space-y-6">
          <Alert>
            <Plus className="h-4 w-4" />
            <AlertDescription>
              追加したい要素やより詳細にしたい部分を指定してください。
              例：NPCの詳細、追加イベント、エンディングバリエーションなど
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>追加生成</CardTitle>
              <CardDescription>
                既存のシナリオに追加したい内容を入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="additional-prompt">追加内容</Label>
                <Textarea
                  id="additional-prompt"
                  value={additionalPrompt}
                  onChange={(e) => setAdditionalPrompt(e.target.value)}
                  placeholder="例：
・主要NPC3人の詳細なバックストーリーを追加
・クライマックスの戦闘シーンに複数の選択肢を追加
・サブクエストを2つ追加
・隠しエンディングを1つ追加"
                  rows={10}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {additionalPrompt.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleAdditionalGeneration}
                disabled={!additionalPrompt.trim() || isRefining}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {isRefining ? '生成中...' : '追加生成を実行'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 修正指示タブ */}
        <TabsContent value="correct" className="space-y-6">
          <Alert>
            <MessageSquare className="h-4 w-4" />
            <AlertDescription>
              シナリオの間違いや不足を指摘して、修正指示を出すことができます。
              具体的な修正内容を日本語で記述してください。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>修正指示</CardTitle>
              <CardDescription>
                現在のシナリオに対する修正指示を入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="correction-instruction">修正内容</Label>
                <Textarea
                  id="correction-instruction"
                  value={correctionInstruction}
                  onChange={(e) => setCorrectionInstruction(e.target.value)}
                  placeholder="例：
・プレイヤー人数を3人から4人に変更
・難易度を初心者向けに調整
・ホラー要素を減らしてファンタジー要素を強化
・戦闘シーンを減らして探索要素を増やす"
                  rows={10}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {correctionInstruction.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleCorrection}
                disabled={!correctionInstruction.trim() || isRefining}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                {isRefining ? '修正中...' : '修正を適用'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}